<template>
  <el-dialog
    v-model="dialogVisible"
    :title="title"
    width="calc(100vw - 32px)"
    class="invite-personal-dialog"
    :before-close="handleCloseDialog"
  >
    <div class="dialog-body-inner">
      <el-form
        ref="addFormRef"
        :model="addForm"
        :rules="formRules"
        label-width="120px"
        class="invite-form"
      >
        <el-form-item
          label="证件号码"
          prop="idCard"
        >
          <el-input
            v-model="addForm.idCard"
            placeholder="请输入受邀人证件号码"
            maxlength="20"
            clearable
            class="form-input"
            @input="handleFormInputChange"
          />
        </el-form-item>
        <el-form-item
          label="手机号码"
          prop="phone"
        >
          <PhoneInput
            v-model="addForm.phone"
            placeholder="请输入受邀人手机号码"
            class="form-input"
            @change="handleFormInputChange"
          />
        </el-form-item>
      </el-form>

      <!-- 受邀人详细信息展示 -->
      <div
        v-if="queryResult"
        class="invitee-info"
      >
        <div class="info-title">
          <span>受邀人详细信息</span>
          <div class="title-actions">
            <el-button
              type="primary"
              link
              size="small"
              :icon="Refresh"
              :loading="queryLoading"
              @click="handleRefreshQuery"
            >
              刷新查询
            </el-button>
            <el-tooltip
              :content="inviteDescription"
              placement="top"
              effect="dark"
            >
              <el-icon class="info-tip-icon">
                <QuestionFilled />
              </el-icon>
            </el-tooltip>
          </div>
        </div>
        <div class="info-content">
          <div class="avatar-section">
            <img
              :src="queryResult.avatar"
              alt="头像"
              class="avatar-image"
            >
          </div>
          <div class="info-details">
            <div class="info-item">
              <span class="info-label">昵称</span>
              <span class="info-value">{{ queryResult.nickName }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">真实姓名</span>
              <span class="info-value">{{ queryResult.realName }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">证件号码</span>
              <span class="info-value">{{ queryResult.idCard }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">手机号</span>
              <span class="info-value">{{ queryResult.phone }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">实名认证</span>
              <el-tag
                :type="getConditionStatusType('verified', queryResult.isVerified)"
                size="small"
                class="verified-status-tag"
              >
                {{ getVerifiedStatusText(queryResult.isVerified) }}
              </el-tag>
            </div>
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="dialog-footer">
        <el-button
          v-if="!queryResult"
          type="primary"
          @click="handleQueryAccount"
        >
          {{ queryButtonText }}
        </el-button>
        <el-button
          v-if="shouldShowConfirmButtonComputed"
          type="primary"
          @click="handleConfirmInvite"
        >
          {{ confirmButtonText }}
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { ElMessage } from 'element-plus'
import { Refresh, QuestionFilled } from '@element-plus/icons-vue'
import { getUserByIdCardAndPhone } from '@/api/userCenter'
import { getSmsCaptcha } from '@/api/utils/captcha'
import PhoneInput from '@/components/PhoneInput/index.vue'
import { GLOBAL_PHONE_PATTERN } from '@/utils/idValidator'

export default {
  name: 'InvitePersonalUserDialog',
  components: {
    Refresh,
    QuestionFilled,
    PhoneInput
  },
  props: {
    // 弹窗显示状态
    modelValue: {
      type: Boolean,
      default: false
    },
    // 弹窗标题
    title: {
      type: String,
      default: '邀请个人用户'
    },
    // 查询按钮文本
    queryButtonText: {
      type: String,
      default: '受邀人账号查询'
    },
    // 确认按钮文本
    confirmButtonText: {
      type: String,
      default: '发起邀请'
    },
    // 是否显示确认按钮的条件判断函数
    shouldShowConfirmButton: {
      type: [Boolean, Function],
      default: null
    },
    // 是否需要已实名认证才能显示发起邀请按钮
    requireVerified: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update:modelValue', 'confirm', 'query-success'],
  data() {
    return {
      // 添加表单数据
      addForm: {
        idCard: '',
        phone: ''
      },
      // 查询结果（受邀人信息）
      queryResult: null,
      // 查询用户信息加载状态
      queryLoading: false,
      // 初始表单值（用于判断是否修改）
      initialForm: {
        idCard: '',
        phone: ''
      },
      // 表单验证规则
      formRules: {
        idCard: [
          { required: true, message: '请输入受邀人证件号码', trigger: 'blur' },
          { pattern: /^[a-zA-Z0-9()]{6,20}$/, message: '请输入正确的证件号码（6-20位字符）', trigger: 'blur' }
        ],
        phone: [
          { required: true, message: '请输入受邀人手机号码', trigger: 'blur' },
          { pattern: GLOBAL_PHONE_PATTERN, message: '请输入正确的手机号码', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.modelValue
      },
      set(val) {
        this.$emit('update:modelValue', val)
      }
    },
    /**
     * 根据传入的配置参数动态生成邀请说明
     */
    inviteDescription() {
      const conditions = []
      
      if (this.requireVerified) {
        conditions.push('已实名认证')
      }
      
      if (conditions.length === 0) {
        return '邀请说明：所有用户都可以被邀请'
      }
      
      return `邀请说明：只有满足${conditions.join('、')}的用户才能被邀请`
    },
    // 是否显示确认按钮
    shouldShowConfirmButtonComputed() {
      if (!this.queryResult) {
        return false
      }
      
      // 如果传入了自定义判断函数，优先使用
      if (this.shouldShowConfirmButton) {
        if (typeof this.shouldShowConfirmButton === 'function') {
          return this.shouldShowConfirmButton(this.queryResult)
        }
        return this.shouldShowConfirmButton
      }
      
      // 根据配置的条件判断
      let canShow = true
      
      // 检查是否需要已实名认证
      if (this.requireVerified) {
        canShow = canShow && this.queryResult.isVerified === '2'
      }
      
      return canShow
    }
  },
  watch: {
    modelValue(newVal) {
      if (newVal) {
        this.initDialog()
      }
    }
  },
  beforeUnmount() {
  },
  methods: {
    /**
     * 初始化弹窗
     */
    initDialog() {
      // 重置表单（设置默认值）
      this.addForm = {
        idCard: '',
        phone: ''
      }
      // 保存初始表单值
      this.initialForm = {
        idCard: '',
        phone: ''
      }
      // 清空查询结果
      this.queryResult = null
      // 清除表单验证
      this.$nextTick(() => {
        if (this.$refs.addFormRef) {
          this.$refs.addFormRef.clearValidate()
        }
      })
    },
    /**
     * 监听表单输入变化
     * 如果身份证或手机号被修改，则清空查询结果
     */
    handleFormInputChange() {
      // 检查身份证或手机号是否与初始值不同
      const idCardChanged = this.addForm.idCard !== this.initialForm.idCard
      const phoneChanged = this.addForm.phone !== this.initialForm.phone
      
      if (idCardChanged || phoneChanged) {
        // 如果表单值发生变化，清空查询结果和相关状态
        this.queryResult = null
      }
    },
    /**
     * 关闭弹窗
     */
    handleCloseDialog() {
      this.dialogVisible = false
      // 重置表单（设置默认值）
      this.addForm = {
        idCard: '',
        phone: ''
      }
      // 清空查询结果
      this.queryResult = null
      // 清除表单验证
      if (this.$refs.addFormRef) {
        this.$refs.addFormRef.clearValidate()
      }
    },
    /**
     * 执行查询用户信息的逻辑
     */
    async doQueryUser() {
      this.queryLoading = true
      try {
        // 调用API查询用户信息
        const response = await getUserByIdCardAndPhone(this.addForm.idCard, this.addForm.phone)
        // 后端返回的数据格式：{ code: 200, data: {...} }
        const userData = response.data || response
        
        if (!userData) {
          ElMessage.error('未找到匹配的用户信息')
          return
        }
        
        // 设置查询结果，映射后端返回的字段
        this.queryResult = {
          userId: userData.id, // 用户ID，用于添加操作
          avatar: userData.avatar || require('@/assets/images/userDefaultAvatar.jpg'), // 默认头像
          nickName: userData.nickName || '',
          realName: userData.realName || '',
          idCard: userData.idCardNo || this.addForm.idCard, // 后端返回的字段是 idCardNo
          phone: userData.phone || this.addForm.phone,
          isVerified: userData.isVerified || '0' // 实名认证状态
        }
        
        // 查询成功后，更新初始表单值，用于后续监听变化
        this.initialForm = {
          idCard: this.addForm.idCard,
          phone: this.addForm.phone
        }
        
        ElMessage.success('查询成功')
        
        // 触发查询成功事件
        this.$emit('query-success', this.queryResult)
      } catch (error) {
        console.error('查询用户信息失败:', error)
      } finally {
        this.queryLoading = false
      }
    },
    /**
     * 进行个人账号查询
     */
    async handleQueryAccount() {
      // 表单验证
      this.$refs.addFormRef.validate(async (valid) => {
        if (valid) {
          await this.doQueryUser()
        } else {
          ElMessage.warning('请填写完整的表单信息')
          return false
        }
      })
    },
    /**
     * 刷新查询用户信息
     */
    async handleRefreshQuery() {
      // 验证表单是否已填写
      if (!this.addForm.idCard || !this.addForm.phone) {
        ElMessage.warning('请先填写身份证号和手机号')
        return
      }
      // 直接执行查询，不需要再次验证表单
      await this.doQueryUser()
    },
    /**
     * 确认邀请
     */
    async handleConfirmInvite() {
      if (!this.queryResult || !this.queryResult.phone) {
        ElMessage.warning('请先查询受邀人信息')
        return
      }

      // 触发确认事件，传递查询结果
      this.$emit('confirm', {
        queryResult: this.queryResult,
        idCard: this.addForm.idCard,
        phone: this.addForm.phone
      })
    },
    /**
     * 获取实名认证状态文本
     * @param {string} isVerified - 实名认证状态值（0未认证 1认证中 2已认证 3未通过）
     * @returns {string} 状态文本
     */
    getVerifiedStatusText(isVerified) {
      const statusMap = {
        '0': '未认证',
        '1': '认证中',
        '2': '已认证',
        '3': '未通过'
      }
      return statusMap[String(isVerified)] || '未知'
    },
    /**
     * 获取实名认证状态标签类型
     * @param {string} isVerified - 实名认证状态值（0未认证 1认证中 2已认证 3未通过）
     * @returns {string} Element Plus 标签类型
     */
    getVerifiedStatusType(isVerified) {
      const status = String(isVerified)
      if (status === '2') {
        return 'success' // 已认证 - 绿色
      } else if (status === '1') {
        return 'warning' // 认证中 - 黄色
      } else if (status === '3') {
        return 'danger' // 未通过 - 红色
      } else {
        return 'info' // 未认证 - 灰色
      }
    },
    /**
     * 根据配置条件统一获取状态标签类型
     * @param {string} conditionType - 条件类型：'verified' | 'admin' | 'salesman' | 'joined'
     * @param {any} value - 对应的值
     * @returns {string} Element Plus 标签类型
     */
    getConditionStatusType(conditionType, value) {
      let isRequired = false
      let isSatisfied = false

      switch (conditionType) {
        case 'verified':
          // 需要已实名认证（isVerified === '2'）
          isRequired = this.requireVerified
          isSatisfied = String(value) === '2'
          // 如果未配置该条件，使用原来的实名认证状态逻辑
          if (!isRequired) {
            return this.getVerifiedStatusType(value)
          }
          break
        default:
          return 'info'
      }

      // 如果未配置该条件，显示灰色（info）
      if (!isRequired) {
        return 'info'
      }

      // 如果配置了该条件，满足条件显示绿色（success），不满足显示黄色（warning）
      return isSatisfied ? 'success' : 'warning'
    }
  }
}
</script>

<style lang="scss" scoped>
/* 参照 InviteEnterpriseDialog.vue */
.invite-personal-dialog {
  :deep(.el-dialog__header) {
    padding: 18px 24px 14px;
    border-bottom: 1px solid var(--el-border-color-lighter);
    .el-dialog__title {
      font-size: 16px;
      font-weight: 600;
    }
    .el-dialog__headerbtn {
      top: 16px;
      width: 24px;
      height: 24px;
    }
  }
  :deep(.el-dialog__body) {
    padding: 24px 24px 20px;
  }
  :deep(.el-dialog__footer) {
    padding: 14px 24px 18px;
    border-top: 1px solid var(--el-border-color-lighter);
  }
}

.dialog-body-inner {
  min-height: 120px;
}

.invite-form {
  :deep(.el-form-item) {
    margin-bottom: 24px;
    display: flex;
    align-items: center;
  }
  :deep(.el-form-item__label) {
    color: #333333;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.5;
    height: auto;
    padding-top: 0;
    padding-bottom: 0;
  }
  :deep(.el-form-item__content) {
    display: flex;
    align-items: center;
  }
  :deep(.el-input__inner) {
    color: #333333;
    font-size: 14px;
  }
  :deep(.form-input .el-input__wrapper),
  :deep(.el-input__wrapper) {
    width: 100%;
    height: 40px;
    border-radius: 6px;
    border: 1px solid #e8e8e8 !important;
    box-shadow: none !important;
    box-sizing: border-box;
    background-color: #fff;
    padding: 0 15px;
    &:hover {
      border-color: #e8e8e8 !important;
    }
    &.is-focus,
    &.is-focused {
      border-color: #1890ff !important;
    }
  }
}

.invitee-info {
  margin-top: 24px;
  padding: 24px;
  background: var(--el-fill-color-light);
  border-radius: 10px;
  border: 1px solid var(--el-border-color-lighter);

  .info-title {
    font-size: 15px;
    font-weight: 600;
    color: var(--el-text-color-primary);
    margin-bottom: 18px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
  }

  .title-actions {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .info-tip-icon {
    font-size: 16px;
    color: #909399;
    cursor: pointer;
    transition: color 0.2s;
    margin-left: 0;
    &:hover {
      color: #409eff;
    }
  }

  .info-content {
    display: flex;
    align-items: flex-start;
    gap: 24px;

    .avatar-section {
      flex-shrink: 0;
      .avatar-image {
        width: 72px;
        height: 72px;
        border-radius: 10px;
        object-fit: cover;
        border: 2px solid var(--el-border-color-lighter);
      }
    }

    .info-details {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 14px;

      .info-item {
        display: flex;
        align-items: center;
        font-size: 14px;
        line-height: 1.5;
        .info-label {
          color: var(--el-text-color-secondary);
          margin-right: 10px;
          min-width: 100px;
          flex-shrink: 0;
        }
        .info-value {
          color: var(--el-text-color-primary);
          font-weight: 500;
          word-break: break-all;
        }
      }
      .verified-status-tag {
        margin-left: 0;
      }
    }
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
}

@media (max-width: 768px) {
  .invite-personal-dialog {
    margin-top: 5vh;

    :deep(.el-dialog__header) {
      padding: 16px 44px 12px 16px;
    }

    :deep(.el-dialog__body) {
      max-height: calc(90vh - 126px);
      padding: 20px 16px 16px;
      overflow-y: auto;
      overscroll-behavior: contain;
    }

    :deep(.el-dialog__footer) {
      padding: 12px 16px 16px;
    }
  }

  .invite-form {
    :deep(.el-form-item) {
      display: block;
      margin-bottom: 20px;
    }

    :deep(.el-form-item__label) {
      display: block;
      width: 100% !important;
      margin-bottom: 8px;
      text-align: left;
    }

    :deep(.el-form-item__content) {
      width: 100%;
      margin-left: 0 !important;
    }
  }

  .invitee-info {
    margin-top: 20px;
    padding: 16px;

    .info-title {
      align-items: flex-start;
      flex-wrap: wrap;
    }

    .title-actions {
      margin-left: auto;
    }

    .info-content {
      flex-direction: column;
      gap: 16px;

      .avatar-section {
        align-self: center;
      }

      .info-details {
        width: 100%;

        .info-item {
          align-items: flex-start;

          .info-label {
            min-width: 84px;
          }

          .info-value {
            min-width: 0;
          }
        }
      }
    }
  }

  .dialog-footer {
    width: 100%;

    :deep(.el-button) {
      flex: 1;
      min-width: 0;
      height: 40px;
      margin-left: 0;
    }
  }
}

@media (max-width: 360px) {
  .invite-personal-dialog {
    width: calc(100vw - 20px) !important;
  }

  .invitee-info {
    .info-title {
      display: block;
    }

    .title-actions {
      justify-content: flex-end;
      margin-top: 8px;
    }

    .info-content .info-details .info-item {
      display: block;

      .info-label {
        display: block;
        margin: 0 0 4px;
      }
    }
  }
}
</style>

<!-- el-dialog is teleported to body, so its outer shell cannot be targeted by scoped styles. -->
<style lang="scss">
.el-dialog.invite-personal-dialog {
  max-width: 560px;
}

@media (max-width: 768px) {
  .el-dialog.invite-personal-dialog {
    margin-top: 5vh;
  }
}

@media (max-width: 360px) {
  .el-dialog.invite-personal-dialog {
    width: calc(100vw - 20px) !important;
  }
}
</style>

